import { injectable, inject } from 'inversify';
import { GetProductsByCategoryUseCase } from '../../../domain/usecases/ProductUseCases';
import type {
  ProductRepository,
  ProductCategoryRepository,
  BranchInventoryRepository,
} from '../../../domain/repositories';
import { ProductWithStock, Product } from '../../../domain/models';
import { TYPES } from '../../../di/types';
import { attachBranchStock, inventoryByProductId } from './branchProductStock';

const GENERAL_CATEGORY_ID = 'general';
const GENERAL_CATEGORY_NAME = 'General';

/**
 * Products by category for POS — **global catalog**, branch stock attached (0 if none).
 */
@injectable()
export class GetProductsByCategoryUseCaseImpl implements GetProductsByCategoryUseCase {
  constructor(
    @inject(TYPES.ProductRepository) private readonly productRepository: ProductRepository,
    @inject(TYPES.ProductCategoryRepository) private readonly categoryRepository: ProductCategoryRepository,
    @inject(TYPES.BranchInventoryRepository) private readonly branchInventoryRepository: BranchInventoryRepository,
  ) {}

  async execute(
    categoryId: string,
    branchId: string,
    page = 1,
    limit = 50,
  ): Promise<{
    products: ProductWithStock[];
    total: number;
    hasMore: boolean;
  }> {
    try {
      if (!categoryId || categoryId.trim().length === 0) {
        throw new Error('Category ID is required');
      }
      if (!branchId || branchId.trim().length === 0) {
        throw new Error('Branch ID is required');
      }
      if (page < 1) {
        throw new Error('Page number must be greater than 0');
      }
      if (limit < 1 || limit > 100) {
        throw new Error('Limit must be between 1 and 100');
      }

      let inventoryMap = inventoryByProductId([]);
      try {
        inventoryMap = inventoryByProductId(
          await this.branchInventoryRepository.getByBranch(branchId),
        );
      } catch {
        console.warn('⚠️ Could not load branch inventory, displaying products with stock 0');
      }

      const isGeneral =
        categoryId === GENERAL_CATEGORY_ID ||
        categoryId.toLowerCase() === GENERAL_CATEGORY_NAME.toLowerCase();

      let catalog: Product[] = [];

      if (isGeneral) {
        let catalogPage = 1;
        let hasMore = true;
        let safety = 0;
        while (hasMore && safety < 100) {
          const batch = await this.productRepository.findAll(branchId, catalogPage, 1000);
          catalog.push(...batch.products.filter((p) => p.isActive));
          hasMore = batch.hasMore && batch.products.length > 0;
          catalogPage += 1;
          safety += 1;
        }
      } else {
        const category = await this.categoryRepository.findById(categoryId);
        if (!category) {
          throw new Error('Category not found');
        }
        if (!category.isActive) {
          throw new Error('Category is not active');
        }
        const result = await this.productRepository.findByCategory(categoryId, branchId, 1, 1000);
        catalog = result.products.filter((p) => p.isActive);
      }

      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const pageSlice = catalog.slice(startIndex, endIndex);

      const productsWithStock: ProductWithStock[] = pageSlice.map((product) =>
        attachBranchStock(product, branchId, inventoryMap.get(product.id) ?? null),
      );

      return {
        products: productsWithStock,
        total: catalog.length,
        hasMore: endIndex < catalog.length,
      };
    } catch (error) {
      console.error('Get products by category failed:', error);
      throw error;
    }
  }
}
